/**
 * Crow’s Nest opportunity “new lead” celebration helpers.
 *
 * First visit seeds a baseline (no party for existing backlog).
 * Later unseen opportunity ids fire once (toast + confetti).
 * Toast copy splits paywall vs supplier-add — never calls a supplier add a paywall.
 */

import { formatOpportunityMixLabel } from './crowsNestOpportunityTotals'

export const CROWS_NEST_CELEBRATED_IDS_KEY =
  'crowsNest.celebratedOpportunityIds'
export const CROWS_NEST_FEED_BASELINE_KEY =
  'crowsNest.opportunityFeedBaselineReady'
export const CROWS_NEST_FEED_POLL_MS = 45_000
export const CROWS_NEST_MAX_STORED_CELEBRATED_IDS = 200

const getStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  return window.localStorage
}

export function readCelebratedOpportunityIds(storage = getStorage()) {
  if (!storage) return new Set()
  try {
    const raw = storage.getItem(CROWS_NEST_CELEBRATED_IDS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.map(String).filter(Boolean))
  } catch {
    return new Set()
  }
}

export function writeCelebratedOpportunityIds(ids, storage = getStorage()) {
  if (!storage) return
  const list = [...ids].map(String).filter(Boolean)
  const trimmed =
    list.length > CROWS_NEST_MAX_STORED_CELEBRATED_IDS
      ? list.slice(list.length - CROWS_NEST_MAX_STORED_CELEBRATED_IDS)
      : list
  try {
    storage.setItem(CROWS_NEST_CELEBRATED_IDS_KEY, JSON.stringify(trimmed))
  } catch {
    // Quota / private mode — celebration is best-effort.
  }
}

export function isOpportunityFeedBaselineReady(storage = getStorage()) {
  if (!storage) return false
  try {
    return storage.getItem(CROWS_NEST_FEED_BASELINE_KEY) === '1'
  } catch {
    return false
  }
}

export function markOpportunityFeedBaselineReady(storage = getStorage()) {
  if (!storage) return
  try {
    storage.setItem(CROWS_NEST_FEED_BASELINE_KEY, '1')
  } catch {
    // ignore
  }
}

/**
 * Diff loaded opportunity ids against celebrated set.
 * Seeds baseline on first run (returns no new ids).
 *
 * @param {Array<{ id?: string }>} opportunities
 * @param {Storage|null} [storage]
 * @returns {{ newOpportunities: object[], celebratedIds: Set<string> }}
 */
export function resolveNewOpportunitiesForCelebration(
  opportunities = [],
  storage = getStorage(),
) {
  const rows = (Array.isArray(opportunities) ? opportunities : []).filter(
    row => row && row.id,
  )
  const ids = rows.map(row => String(row.id))
  const celebrated = readCelebratedOpportunityIds(storage)

  if (!isOpportunityFeedBaselineReady(storage)) {
    ids.forEach(id => celebrated.add(id))
    writeCelebratedOpportunityIds(celebrated, storage)
    markOpportunityFeedBaselineReady(storage)
    return { newOpportunities: [], celebratedIds: celebrated }
  }

  const newOpportunities = rows.filter(row => !celebrated.has(String(row.id)))
  if (newOpportunities.length > 0) {
    newOpportunities.forEach(row => celebrated.add(String(row.id)))
    writeCelebratedOpportunityIds(celebrated, storage)
  }

  return { newOpportunities, celebratedIds: celebrated }
}

/**
 * Classify a mapped or raw opportunity for toast mix copy.
 * @param {object} row
 * @returns {'paywall'|'supplier_added'|'supplier_milestone'|'other'}
 */
export function classifyOpportunityCelebrationKind(row = {}) {
  const type = row.type
  if (type === 'paywall_limit') return 'paywall'
  if (type === 'supplier_added') return 'supplier_added'
  if (type === 'supplier_milestone') return 'supplier_milestone'

  const label = String(row.typeLabel || '')
  if (label === 'Paywall') return 'paywall'
  if (label === 'Supplier add') return 'supplier_added'
  if (label === 'Supplier milestone') return 'supplier_milestone'
  return 'other'
}

export function countCelebratedOpportunitiesByKind(opportunities = []) {
  const counts = {
    paywall: 0,
    supplier_added: 0,
    supplier_milestone: 0,
    other: 0,
  }
  for (const row of opportunities) {
    counts[classifyOpportunityCelebrationKind(row)] += 1
  }
  return counts
}

function celebrationHeadline(count, counts) {
  const paywalls = counts.paywall
  const supplierAdds = counts.supplier_added
  const milestones = counts.supplier_milestone
  const supplierSignals = supplierAdds + milestones

  if (paywalls === count) {
    return count === 1
      ? 'New Crow’s Nest paywall!'
      : `${count} new Crow’s Nest paywalls!`
  }

  if (supplierSignals === count && paywalls === 0) {
    if (count === 1 && supplierAdds === 1) {
      return 'New Crow’s Nest supplier add!'
    }
    if (count === 1 && milestones === 1) {
      return 'New Crow’s Nest supplier milestone!'
    }
    return `${count} new Crow’s Nest supplier signals!`
  }

  const mix = formatOpportunityMixLabel({
    opportunities: count,
    paywallLimit: paywalls,
    supplierAdded: supplierAdds,
    supplierMilestone: milestones,
  }).replace(/^\d+ leads? · /, '')

  return `${count} new Crow’s Nest leads — ${mix}`
}

/**
 * Fun copy for the toast — keep it light for the sales crew.
 * Supplier-add copy never uses the word paywall.
 * @param {object[]} newOpportunities
 */
export function buildOpportunityCelebrationToast(newOpportunities = []) {
  const count = newOpportunities.length
  if (count <= 0) {
    return null
  }

  const newest = [...newOpportunities].sort((a, b) => {
    const aTime = a.occurredAt ? new Date(a.occurredAt).getTime() : 0
    const bTime = b.occurredAt ? new Date(b.occurredAt).getTime() : 0
    return bTime - aTime
  })[0]

  const venue = newest.venueName || 'A venue'
  const signal = newest.typeLabel || newest.featureLabel || 'lead'

  const detailParts = [newest.typeLabel, newest.featureLabel].filter(
    (part, index, all) => part && all.indexOf(part) === index,
  )
  const detail = detailParts.length ? detailParts.join(' · ') : signal
  const counts = countCelebratedOpportunitiesByKind(newOpportunities)
  const message = celebrationHeadline(count, counts)

  if (count === 1) {
    return {
      message,
      description: `${venue} — ${detail}`,
    }
  }

  return {
    message,
    description: `Latest: ${venue} — ${detail}. Nice work, team.`,
  }
}
